import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, orderBy, getDocs } from "firebase/firestore"; 
import { useNavigate } from 'react-router-dom';
import XLSX from 'xlsx-js-style';

const safeNum = (num) => {
    const val = parseFloat(num || 0);
    return parseFloat(val.toFixed(2));
};

const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatWeight = (weight) => {
    if (!weight) return '-';
    const val = parseFloat(weight);
    if (isNaN(val)) return '-';
    return Number.isInteger(val) ? `${val.toFixed(1)} KG` : `${val} KG`;
};

const formatMonthYear = (dateString) => { 
    if (!dateString) return "";
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase(); 
};

function numberToWordsBDT(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsBDTInternal = (n) => {
        let str = '';
        if (n > 9999999) { str += inWordsBDTInternal(Math.floor(n / 10000000)); str += 'Crore '; n %= 10000000; }
        if (n > 99999) { str += inWordsBDTInternal(Math.floor(n / 100000)); str += 'Lakh '; n %= 100000; }
        if (n > 999) { str += inWordsBDTInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsBDTInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    const taka = Math.round(parseFloat(num || 0));
    if (taka === 0) return 'BDT ZERO TAKA ONLY';
    let takaWords = inWordsBDTInternal(taka).trim(); 
    let result = 'BDT ' + takaWords + ' Taka Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase(); 
}

function numberToWordsUSD(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsInternal = (n) => {
        let str = '';
        if (n > 999999999) { str += inWordsInternal(Math.floor(n / 1000000000)); str += 'Billion '; n %= 1000000000; }
        if (n > 999999) { str += inWordsInternal(Math.floor(n / 1000000)); str += 'Million '; n %= 1000000; }
        if (n > 999) { str += inWordsInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    num = parseFloat(num || 0).toFixed(2);
    if (num === '0.00') return 'USD ZERO DOLLARS ONLY';
    const [dollars, cents] = num.split('.');
    let dollarWords = inWordsInternal(parseInt(dollars)).trim(); 
    let centWords = inWordsInternal(parseInt(cents)).trim(); 
    let result = 'USD ';
    if (dollarWords) { result += dollarWords + ' Dollars'; }
    if (centWords && parseInt(cents) > 0) { if (dollarWords) { result += ' and '; } result += centWords + ' Cents'; }
    result += ' Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase(); 
}

const calculateItemTotalUsd = (item, exchangeRate) => {
    if (item.rateCurrency === 'BDT') {
        const rate = parseFloat(item.rate || 0);
        const weight = parseFloat(item.weight || 0);
        const totalBdtForItem = rate * weight;
        const exRate = parseFloat(exchangeRate || 100) || 1;
        return totalBdtForItem / exRate;
    }
    const rate = parseFloat(item.rate || 0);
    const fsc = parseFloat(item.fsc || 0);
    const demandTotal = parseFloat(item.demandCharge || 0);
    return (rate + fsc + demandTotal);
};

const months = [
    { value: "01", name: "January" }, { value: "02", name: "February" }, { value: "03", name: "March" },
    { value: "04", name: "April" }, { value: "05", name: "May" }, { value: "06", name: "June" },
    { value: "07", name: "July" }, { value: "08", name: "August" }, { value: "09", name: "September" },
    { value: "10", name: "October" }, { value: "11", name: "November" }, { value: "12", name: "December" }
];
const currentYear = new Date().getFullYear(); 
const years = Array.from({length: 21}, (_, i) => currentYear - 10 + i); 

const CombinedReport = () => {
    const navigate = useNavigate();
    const [companyList, setCompanyList] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [startMonth, setStartMonth] = useState('01');
    const [startYear, setStartYear] = useState(currentYear); 
    const [endMonth, setEndMonth] = useState(months[new Date().getMonth()].value);
    const [endYear, setEndYear] = useState(currentYear);      
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const signatureDetails = {
        'MD. MORSHED': {
            name: 'MD. MORSHED',
            phone: '+880 1750-021581',
            email: null
        },
        'ANIS KHAN': {
            name: 'Anis Khan',
            phone: '+880 1791-479940',
            email: 'aronnoanis4u@gmail.com'
        }
    };

    useEffect(() => {
        const originalTitle = document.title;
        document.title = "Combined Invoice Report";
        return () => { document.title = originalTitle; };
    }, []);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const q = query(collection(db, 'companies'), orderBy('name'));
                const querySnapshot = await getDocs(q);
                const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCompanyList(list);
            } catch (error) { console.error("Error fetching companies: ", error); }
        };
        fetchCompanies();
    }, []);

    const handleLoadReport = async () => {
        if (!selectedCompanyId) {
            setMessage('Please select a company.');
            return;
        }
        setLoading(true);
        setMessage('');
        setInvoices([]);
        const startYearMonth = `${startYear}-${startMonth}`;
        const endYearMonth = `${endYear}-${endMonth}`;
        
        try {
            const invoicesQuery = query(
                collection(db, 'invoices'),
                where('companyId', '==', selectedCompanyId),
                where('invoiceYearMonth', '>=', startYearMonth),
                where('invoiceYearMonth', '<=', endYearMonth),
                orderBy('invoiceYearMonth', 'asc') 
            );
            const querySnapshot = await getDocs(invoicesQuery);
            const loadedInvoices = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            if (loadedInvoices.length === 0) {
                setMessage('No invoices found for this company in the selected range.');
            }
            
            setInvoices(loadedInvoices);
            if (loadedInvoices.length > 0) {
                const companyName = companyList.find(c => c.id === selectedCompanyId)?.name || "Report";
                document.title = `Report - ${companyName}`;
            } else {
                document.title = "No Report Found";
            }
        } catch (error) {
            console.error("Error fetching combined report: ", error);
            setMessage(`Error loading report. Firebase error: ${error.message}`);
        }
        setLoading(false);
    };

    const handleDownloadExcel = () => {
        if (invoices.length === 0) return;
        const styles = {
            header: { font: { name: "Times New Roman", sz: 20, bold: true }, alignment: { horizontal: "center", vertical: "center" } },
            address: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" } },
            label: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            value: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            invoiceLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            invoiceValue: { font: { name: "Times New Roman", sz: 11, bold: true, color: { rgb: "FF0000" } }, alignment: { horizontal: "left", vertical: "center" } },
            dateValue: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            billTitle: { font: { name: "Times New Roman", sz: 14, bold: true, underline: true }, alignment: { horizontal: "center" } },
            tableHeader: { font: { name: "Calibri", sz: 11, bold: true }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, fill: { fgColor: { rgb: "F2F2F2" } } },
            cellCenter: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } } },
            cellRight: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } } },
            cellCurrencyUSD: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '"$ "#,##0.00' },
            cellCurrencyRateBDT: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '"৳ "#,##0.00' },
            cellWeight: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '0.0' },
            cellCurrencyBDT: { font: { name: "Calibri", sz: 11, bold: true }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '#,##0.00' },
            summaryLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" } },
            summaryValueUSD: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" }, numFmt: '"$ "#,##0.00' },
            summaryValueBDT: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" }, numFmt: '#,##0.00' },
            finalTotal: { font: { name: "Times New Roman", sz: 12, bold: true }, alignment: { horizontal: "right" }, border: { top: { style: "medium" }, bottom: { style: "double" } }, numFmt: '#,##0.00' },
            inWordLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left" } },
            inWordValue: { font: { name: "Times New Roman", sz: 11, italic: true }, alignment: { horizontal: "left", wrapText: true } }
        };

        const allRows = [];
        const allMerges = [];
        const pageBreaks = [];
        let currentRowIndex = 0;

        invoices.forEach((invoice) => {
            const startRow = currentRowIndex;
            const invoiceRows = [];
            
            invoiceRows.push([{ v: "TWS AIR INTERNATIONAL", s: styles.header }, "", "", "", "", "", "", "", "", "", ""]);
            invoiceRows.push([{ v: "10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.", s: styles.address }, "", "", "", "", "", "", "", "", "", ""]);
            invoiceRows.push([]);
            
            const dateStr = new Date(invoice.invoiceDate).toLocaleDateString('en-GB');
            const rLabelIdx = 7;
            const rValueIdx = 8;
            let row1 = new Array(11).fill("");
            row1[0] = { v: "COMPANY NAME", s: styles.label };
            row1[1] = { v: ": " + invoice.companyName, s: styles.value };
            row1[rLabelIdx] = { v: "DATE", s: styles.invoiceLabel };
            row1[rValueIdx] = { v: ": " + dateStr, s: styles.dateValue };
            invoiceRows.push(row1);
            let row2 = new Array(11).fill("");
            row2[0] = { v: "CUSTOMER NAME", s: styles.label };
            row2[1] = { v: ": " + invoice.customerName, s: styles.value };
            invoiceRows.push(row2);
            let row3 = new Array(11).fill("");
            row3[0] = { v: "ADDRESS", s: styles.label };
            row3[1] = { v: ": " + invoice.customerAddress, s: styles.value };
            row3[rLabelIdx] = { v: "INVOICE NO", s: styles.invoiceLabel };
            row3[rValueIdx] = { v: ": " + invoice.invoiceNo, s: styles.invoiceValue };
            invoiceRows.push(row3);
            let row4 = new Array(11).fill("");
            row4[0] = { v: "PHONE", s: styles.label };
            row4[1] = { v: ": " + invoice.customerPhone, s: styles.value };
            invoiceRows.push(row4);
            invoiceRows.push([]);
            
            invoiceRows.push([{ v: `COURIER BILL - ${formatMonthYear(invoice.invoiceYearMonth)}`, s: styles.billTitle }, "", "", "", "", "", "", "", "", "", ""]);
            invoiceRows.push([]);
            
            let colHeaders = ["DATE", "COUNTRY", "AWB NO."];
            if (invoice.showBuyerNameInView) colHeaders.push("BUYER");
            colHeaders.push("SHIPMENT", "SERVICE", "WEIGHT");
            if (invoice.showUsdInView) {
                if (invoice.showDemandInView) colHeaders.push("RATE", "DEMAND ($)");
                else colHeaders.push("RATE");
                colHeaders.push("FSC ($)", "TOTAL ($)");
            }
            if (invoice.showBdtInView) colHeaders.push("TOTAL (BDT)");
            const headerRow = colHeaders.map(h => ({ v: h, s: styles.tableHeader }));
            invoiceRows.push(headerRow);
            
            invoice.items.forEach(item => {
                const row = [];
                row.push({ v: item.date, s: styles.cellCenter });
                row.push({ v: item.country, s: styles.cellCenter });
                row.push({ v: item.awbNo, s: styles.cellCenter });
                if (invoice.showBuyerNameInView) row.push({ v: item.buyerName || '-', s: styles.cellCenter });
                row.push({ v: item.shipment, s: styles.cellCenter });
                row.push({ v: item.service, s: styles.cellCenter });
                row.push({ v: parseFloat(item.weight || 0), s: styles.cellWeight });
                
                const rate = parseFloat(item.rate || 0);
                const fsc = parseFloat(item.fsc || 0);
                const demandTotal = parseFloat(item.demandCharge || 0);
                
                const isBdtRate = item.rateCurrency === 'BDT';
                const totalUsdForItem = calculateItemTotalUsd(item, invoice.exchangeRate);
                const rateStyle = isBdtRate ? styles.cellCurrencyRateBDT : styles.cellCurrencyUSD;

                if (invoice.showUsdInView) {
                    if (invoice.showDemandInView) {
                        row.push({ v: safeNum(rate), s: rateStyle });
                        row.push({ v: safeNum(demandTotal), s: styles.cellCurrencyUSD });
                    } else {
                        row.push({ v: safeNum(rate + demandTotal), s: rateStyle });
                    }
                    row.push({ v: safeNum(fsc), s: styles.cellCurrencyUSD });
                    row.push({ v: safeNum(totalUsdForItem), s: styles.cellCurrencyUSD });
                }
                
                if (invoice.showBdtInView) {
                    row.push({ v: safeNum(totalUsdForItem * (invoice.exchangeRate || 0)), s: styles.cellCurrencyBDT });
                }
                invoiceRows.push(row);
            });
            invoiceRows.push([]);
            
            const totalColIndex = headerRow.length - 1;
            const labelColIndex = totalColIndex - 1;
            const addSummaryRow = (label, value, isFinal = false, isDiscount = false, isUsd = false) => {
                const row = new Array(headerRow.length).fill("");
                let valStyle = isUsd ? styles.summaryValueUSD : styles.summaryValueBDT;
                if (isFinal) valStyle = styles.finalTotal;
                if (isDiscount) valStyle = { ...valStyle, font: { ...valStyle.font, color: { rgb: "FF0000" } } };
                row[labelColIndex] = { v: label, s: styles.summaryLabel };
                row[totalColIndex] = { v: safeNum(value), s: valStyle };
                invoiceRows.push(row);
            };
            if (invoice.showUsdInView) {
                addSummaryRow("TOTAL USD:", invoice.totalUsd, false, false, true);
            }
            if (invoice.showBdtInView) {
                addSummaryRow("TOTAL BDT:", invoice.totalBdt);
                addSummaryRow("PREVIOUS DUE:", invoice.previousDue);
                if (invoice.discountAmount > 0) {
                    addSummaryRow("DISCOUNT:", invoice.discountAmount, false, true);
                }
                if (invoice.paidEntries && invoice.paidEntries.length > 0) {
                    invoice.paidEntries.forEach(entry => {
                        addSummaryRow(`PAID (${entry.date}):`, entry.amount);
                    });
                }
                addSummaryRow("FINAL TOTAL:", invoice.finalTotalBdt, true);
            }
            invoiceRows.push([]);
            
            const inWordText = invoice.showBdtInView ? numberToWordsBDT(invoice.finalTotalBdt) : numberToWordsUSD(invoice.totalUsd);
            invoiceRows.push([
                { v: "IN WORD:", s: styles.inWordLabel },
                { v: inWordText, s: styles.inWordValue }
            ]);
            invoiceRows.push([]);
            invoiceRows.push([]);
            
            const signedBy = invoice.signedBy || 'MD. MORSHED';
            const currentSigner = signatureDetails[signedBy] || signatureDetails['MD. MORSHED'];
            invoiceRows.push([{ v: "Best Regards,", s: styles.label }]);
            invoiceRows.push([{ v: currentSigner.name, s: { font: { bold: true, sz: 12 } } }]);
            invoiceRows.push([{ v: `Phone: ${currentSigner.phone}`, s: styles.value }]);
            if(currentSigner.email) {
                invoiceRows.push([{ v: `Email: ${currentSigner.email}`, s: styles.value }]);
            }
            invoiceRows.push([]); 
            
            if (invoice.showPaymentFooter !== false) {
                invoiceRows.push([{ v: "ACCOUNT INFORMATION:", s: { font: { bold: true, underline: true } } }]);
                const addAccRow = (label, val) => {
                    let r = new Array(11).fill("");
                    r[0] = { v: label, s: styles.label };
                    r[1] = { v: ": " + val, s: styles.value };
                    invoiceRows.push(r);
                };
                addAccRow("Bank Name", "BRAC BANK");
                addAccRow("Account Name", "ARONNO ANIS KHAN");
                addAccRow("Account Number", "1071909180001");
                addAccRow("Branch", "DEMRA SME BRANCH");
                addAccRow("Routing Number", "060271495");
                addAccRow("BKash/Nagad", "+88015702-72101");
            }
            
            allRows.push(...invoiceRows);
            
            allMerges.push({ s: { r: startRow + 0, c: 0 }, e: { r: startRow + 0, c: headerRow.length - 1 } });
            allMerges.push({ s: { r: startRow + 1, c: 0 }, e: { r: startRow + 1, c: headerRow.length - 1 } });
            [3, 4, 5, 6].forEach(offset => {
                allMerges.push({ s: { r: startRow + offset, c: 1 }, e: { r: startRow + offset, c: 5 } });
            });
            [3, 5].forEach(offset => {
                allMerges.push({ s: { r: startRow + offset, c: 8 }, e: { r: startRow + offset, c: headerRow.length - 1 } });
            });
            const billTitleRowIndex = invoiceRows.findIndex(r => r[0] && r[0].v && r[0].v.toString().startsWith("COURIER BILL"));
            if(billTitleRowIndex !== -1) {
                allMerges.push({ s: { r: startRow + billTitleRowIndex, c: 0 }, e: { r: startRow + billTitleRowIndex, c: headerRow.length - 1 } });
            }
            const inWordRowIndex = invoiceRows.findIndex(r => r[0] && r[0].v === "IN WORD:");
            if (inWordRowIndex !== -1) {
                allMerges.push({ s: { r: startRow + inWordRowIndex, c: 1 }, e: { r: startRow + inWordRowIndex, c: headerRow.length - 1 } });
            }
            const startPayIdx = invoiceRows.findIndex(r => r[0] && r[0].v === "Bank Name");
            if(startPayIdx !== -1) {
                for(let i=0; i<6; i++) {
                    allMerges.push({ s: { r: startRow + startPayIdx + i, c: 1 }, e: { r: startRow + startPayIdx + i, c: 4 } });
                }
            }
            allRows.push([]);
            allRows.push([]);
            currentRowIndex += invoiceRows.length + 2; 
            pageBreaks.push({ r: currentRowIndex - 1 });
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(allRows);
        worksheet['!merges'] = allMerges;
        worksheet['!rowbreaks'] = pageBreaks;
        worksheet['!pageSetup'] = { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToHeight: 0, fitToWidth: 1 };
        worksheet['!margins'] = { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };
        
        const MAX_COL_WIDTH = 30;
        const columnWidths = [];
        const sampleHeaderIdx = allRows.findIndex(r => r[0] && r[0].v === "DATE");
        if (sampleHeaderIdx !== -1) { 
            for (let r = sampleHeaderIdx; r < allRows.length; r++) {
                const row = allRows[r];
                if (!row) continue;
                row.forEach((cell, i) => {
                    let cellValue = "";
                    if (typeof cell === 'object' && cell !== null && cell.v !== undefined) cellValue = cell.v.toString();
                    else if (cell !== undefined && cell !== null) cellValue = cell.toString();
                    const cellLength = cellValue.length;
                    if (!columnWidths[i] || cellLength > columnWidths[i]) columnWidths[i] = cellLength;
                });
            }
        }
        worksheet['!cols'] = columnWidths.map((w, i) => {
            if (i === 0) return { wch: 16 };
            const finalW = Math.min(w + 2, MAX_COL_WIDTH);
            return { wch: Math.max(finalW, 10) };
        });

        const companyNameSafe = invoices[0]?.companyName.replace(/[^a-z0-9]/gi, '_').toUpperCase() || "COMBINED";
        const fileName = `${companyNameSafe}_Combined_Report.xlsx`;
        XLSX.utils.book_append_sheet(workbook, worksheet, "Combined_Report");
        XLSX.writeFile(workbook, fileName);
    };

    return (
        <div className='view-invoice-container'>
            <p className='new-invoice-heading'>Combined Invoice Report</p>
            <div className='form-section combined-report-selectors'>
                <h3>Select Range</h3>
                <div className='form-grid' style={{gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', alignItems: 'flex-end'}}>
                    <div>
                        <label>Company:</label>
                        <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}>
                            <option value="">-- Select Company --</option>
                            {companyList.map(comp => (<option key={comp.id} value={comp.id}>{comp.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label>Start Month:</label>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <select value={startMonth} onChange={e => setStartMonth(e.target.value)}>
                                {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                            </select>
                            <select value={startYear} onChange={e => setStartYear(e.target.value)}>
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label>End Month:</label>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <select value={endMonth} onChange={e => setEndMonth(e.target.value)}>
                                {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                            </select>
                            <select value={endYear} onChange={e => setEndYear(e.target.value)}>
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                <button onClick={handleLoadReport} disabled={loading} className='add-btn' style={{marginTop: '20px'}}>
                    {loading ? 'Loading...' : 'Load Report'}
                </button>
                {message && <p style={{marginTop: '10px', color: 'red'}}>{message}</p>}
            </div>

            {invoices.length > 0 && (
                <div className="invoice-actions combined-report-actions" style={{justifyContent: 'center'}}>
                    <button onClick={() => navigate(-1)} style={{backgroundColor: '#6c757d', marginRight: '10px'}}>Back</button>
                    <button onClick={() => window.print()}>Print Combined Report</button>
                    <button onClick={handleDownloadExcel} style={{backgroundColor: '#217346'}}>Download Excel</button>
                </div>
            )}
            
            <div className="combined-report-area">
                {invoices.map(invoice => {
                    const signedBy = invoice.signedBy || 'MD. MORSHED';
                    const showPaymentFooter = invoice.showPaymentFooter !== undefined ? invoice.showPaymentFooter : true;
                    const showBuyerName = invoice.showBuyerNameInView || false;
                    const invoiceFooterTextOption = invoice.invoiceFooterText || 'Final';
                    const currentSigner = signatureDetails[signedBy] || signatureDetails['MD. MORSHED'];

                    return (
                        <div key={invoice.id} className="combined-invoice-page">
                            <div className="invoice-paper">
                                <div className="invoice-header">
                                    <h1>TWS AIR INTERNATIONAL</h1>
                                    <p>10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.</p>
                                </div>
                                
                                <div className="customer-details">
                                    <div>
                                        <p><strong>COMPANY NAME: {invoice.companyName}</strong></p>
                                        <p><strong>CUSTOMER NAME: {invoice.customerName}</strong></p>
                                        <p><strong>ADDRESS: {invoice.customerAddress}</strong></p>
                                        <p><strong>PHONE: {invoice.customerPhone}</strong></p>
                                    </div>
                                    <div>
                                        <p><strong>INVOICE NO:</strong> <span style={{color: 'red', fontWeight: 'bold'}}>{invoice.invoiceNo || 'N/A'}</span></p>
                                        <p><strong>DATE:</strong> {new Date(invoice.invoiceDate).toLocaleDateString('en-GB')}</p>
                                    </div>
                                </div>

                                <h3 className="bill-title">COURIER BILL - {formatMonthYear(invoice.invoiceYearMonth)}</h3>

                                <table className="invoice-items-table">
                                    <thead>
                                        <tr>
                                            <th>DATE</th>
                                            <th>COUNTRY</th>
                                            <th>AWB NO.</th>
                                            {showBuyerName && <th>BUYER</th>}
                                            <th>SHIPMENT</th>
                                            <th>SERVICE</th>
                                            <th>WEIGHT</th>
                                            {invoice.showUsdInView && (
                                                <>
                                                    <th>RATE</th>
                                                    {invoice.showDemandInView && <th>DEMAND ($)</th>}
                                                    <th>FSC ($)</th>
                                                    <th>TOTAL ($)</th>
                                                </>
                                            )}
                                            {invoice.showBdtInView && <th>TOTAL (BDT)</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.items && invoice.items.map((item, index) => {
                                            const rate = parseFloat(item.rate || 0);
                                            const fsc = parseFloat(item.fsc || 0);
                                            const demandTotal = parseFloat(item.demandCharge || 0);
                                            const isBdtRate = item.rateCurrency === 'BDT';
                                            const totalUsdForItem = calculateItemTotalUsd(item, invoice.exchangeRate);

                                            return (
                                                <tr key={index}>
                                                    <td>{item.date}</td>
                                                    <td>{item.country}</td>
                                                    <td>{item.awbNo}</td>
                                                    {showBuyerName && <td>{item.buyerName || '-'}</td>}
                                                    <td>{item.shipment}</td>
                                                    <td>{item.service}</td>
                                                    <td style={{whiteSpace: 'nowrap'}}>{formatWeight(item.weight)}</td>
                                                    
                                                    {invoice.showUsdInView && (
                                                        <>
                                                            {invoice.showDemandInView ? (
                                                                <td>{isBdtRate ? '৳' : '$'} {formatCurrency(rate)}</td>
                                                            ) : (
                                                                <td>{isBdtRate ? '৳' : '$'} {formatCurrency(rate + demandTotal)}</td>
                                                            )}
                                                            {invoice.showDemandInView && <td>$ {formatCurrency(demandTotal)}</td>}
                                                            <td>$ {formatCurrency(fsc)}</td>
                                                            <td>$ {formatCurrency(totalUsdForItem)}</td>
                                                        </>
                                                    )}
                                                    
                                                    {invoice.showBdtInView && (
                                                        <td>{formatCurrency(totalUsdForItem * invoice.exchangeRate)}</td>
                                                    )}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>

                                <div className="invoice-summary-view">
                                    <div className="summary-left">
                                        <div className="in-word-container">
                                            <strong>IN WORD: </strong>
                                            <span>
                                                {invoice.showBdtInView ? numberToWordsBDT(invoice.finalTotalBdt) : numberToWordsUSD(invoice.totalUsd)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="summary-right">
                                        {invoice.showUsdInView && (
                                            <p><span>TOTAL IN USD</span> <span>$ {formatCurrency(invoice.totalUsd)}</span></p>
                                        )}
                                        {invoice.showBdtInView ? (
                                            <>
                                                <p><span>TOTAL IN BDT</span> <span>{formatCurrency(invoice.totalBdt)}</span></p>
                                                <p><span>PREVIOUS DUE</span> <span>{formatCurrency(invoice.previousDue)}</span></p>
                                                {invoice.discountAmount > 0 && (
                                                    <p><span>DISCOUNT</span> <span>- {formatCurrency(invoice.discountAmount)}</span></p>
                                                )}
                                                {invoice.paidEntries && invoice.paidEntries.map((entry, index) => (
                                                    <p key={index}><span>{entry.date} PAID</span> <span>- {formatCurrency(entry.amount)}</span></p>
                                                ))}
                                                <p className="final-total-view">
                                                    <span>TOTAL IN BDT</span> <span>{formatCurrency(invoice.finalTotalBdt)}</span>
                                                </p>
                                            </>
                                        ) : (
                                            <p className="final-total-view">
                                                <span>TOTAL IN USD</span> <span>$ {formatCurrency(invoice.totalUsd)}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="invoice-footer" style={{ marginTop: '50px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <div style={{ marginBottom: '20px' }}>
                                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Best Regards,</p>
                                        <p style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{currentSigner.name}</p>
                                        <p>{currentSigner.phone}</p>
                                        {currentSigner.email && <p>Email: {currentSigner.email}</p>}
                                    </div>
                                    {showPaymentFooter && (
                                        <div style={{ marginTop: '20px', fontSize: '12.5px', color: '#333' }}>
                                            <p style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '8px' }}>Account Information:</p>
                                            <div style={{ display: 'grid', gridTemplateColumns: '120px 10px 1fr', rowGap: '4px' }}>
                                                <span style={{ textAlign: 'left' }}>Bank Name</span> <span>:</span> <strong>BRAC BANK</strong>
                                                <span style={{ textAlign: 'left' }}>Account Name</span> <span>:</span> <strong>ARONNO ANIS KHAN</strong>
                                                <span style={{ textAlign: 'left' }}>Account Number</span> <span>:</span> <strong>1071909180001</strong>
                                                <span style={{ textAlign: 'left' }}>Branch</span> <span>:</span> <strong>DEMRA SME BRANCH</strong>
                                                <span style={{ textAlign: 'left' }}>Routing Number</span> <span>:</span> <strong>060271495</strong>
                                                <span style={{ textAlign: 'left' }}>BKash/Nagad</span> <span>:</span> <strong>+88015702-72101</strong>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* --- FOOTER TEXT CONDITIONAL RENDERING --- */}
                                {invoiceFooterTextOption === 'Demo' ? (
                                    <div style={{
                                        textAlign: 'center', 
                                        marginTop: '40px', 
                                        fontWeight: 'bold', 
                                        fontSize: '13px', 
                                        color: '#e74c3c', 
                                        border: '1px dashed #e74c3c', 
                                        padding: '10px', 
                                        borderRadius: '5px'
                                    }}>
                                        This is a demo invoice for estimation purposes only. The charges shown are approximate and provided to give an idea of the bill. The final invoice with actual charges will be issued later.
                                    </div>
                                ) : invoiceFooterTextOption === 'Final' ? (
                                    <div style={{
                                        textAlign: 'center', 
                                        marginTop: '40px', 
                                        fontSize: '12px', 
                                        color: '#555', 
                                        fontStyle: 'italic'
                                    }}>
                                        This is a system-generated invoice and does not require a signature. If you find any discrepancy, please notify us within 15 days of receiving this invoice. Otherwise, the invoice shall be deemed accepted and considered final.
                                    </div>
                                ) : (
                                    <div style={{
                                        textAlign: 'center', 
                                        marginTop: '40px', 
                                        fontWeight: 'bold', 
                                        fontSize: '14px', 
                                        color: '#555'
                                    }}>
                                        Thank you for your business!
                                    </div>
                                )}

                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default CombinedReport;